import { useState, useEffect, useMemo } from "react"
import { Search, Filter, X } from "lucide-react"
import { useCategories } from "../hooks/useCategories"

export default function SearchBar({ 
  placeholder = "Rechercher des cours...", 
  onSearch, 
  onChange, // Callback appelée quand le terme de recherche change
  showFilters = true,
  className = "",
  variant = "default", // "default" ou "hero"
  defaultValue = "", // Valeur initiale pour la recherche
  showSearchButton = true // Afficher le bouton "Rechercher" (par défaut true pour la variante hero)
}) {
  const [searchTerm, setSearchTerm] = useState(defaultValue || "")
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [filters, setFilters] = useState({
    category: "",
    level: "",
    price: "",
    duration: "",
    rating: ""
  })

  // Récupérer les vraies catégories depuis l'API
  const { categories: apiCategories } = useCategories()
  
  // Mapper les catégories API en format simple pour les filtres (pas de fallback mocké)
  const categories = useMemo(() => {
    return [
      "Toutes les catégories",
      ...(apiCategories && apiCategories.length > 0 
        ? apiCategories.map(cat => cat.displayName || cat.translatedName || cat.name).filter(Boolean)
        : []
      )
    ]
  }, [apiCategories])

  // Mettre à jour searchTerm quand defaultValue change (depuis les paramètres d'URL)
  useEffect(() => {
    if (defaultValue !== undefined) {
      setSearchTerm(defaultValue)
    }
  }, [defaultValue])

  const levels = [
    "Tous les niveaux",
    "Débutant",
    "Intermédiaire",
    "Avancé"
  ]

  const priceRanges = [
    "Tous les prix",
    "Gratuit",
    "0-50000 FCFA",
    "50000-100000 FCFA",
    "100000+ FCFA"
  ]

  const durations = [
    "Toutes les durées",
    "Moins de 2h",
    "2-5h",
    "5-10h",
    "10h+"
  ]

  const ratings = [
    "Toutes les notes",
    "4+ étoiles",
    "3+ étoiles",
    "2+ étoiles"
  ]

  const handleSearch = (e) => {
    e.preventDefault()
    if (onSearch) {
      if (variant === "hero") {
        onSearch(searchTerm)
      } else {
        onSearch({
          term: searchTerm,
          filters: filters
        })
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e)
    }
  }

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      category: "",
      level: "",
      price: "",
      duration: "",
      rating: ""
    })
  }

  const hasActiveFilters = Object.values(filters).some(value => value !== "")

  // Rendu pour la variante hero
  if (variant === "hero") {
    return (
      <form onSubmit={handleSearch} className={`relative flex-1 max-w-2xl group ${className}`}>
        {showSearchButton ? (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-gray-900 transition-colors duration-300" />
              <input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  if (onChange) {
                    onChange(e.target.value)
                  }
                }}
                onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 transition-all duration-300 bg-white"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-[#203a8a] hover:bg-[#1a1f4a] text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:ring-offset-2 whitespace-nowrap"
            >
              Rechercher
            </button>
          </div>
        ) : (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-gray-900 transition-colors duration-300" />
            <input
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                if (onChange) {
                  onChange(e.target.value)
                }
              }}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 transition-all duration-300 bg-white"
            />
          </div>
        )}
      </form>
    )
  }

  // Rendu par défaut (avec filtres)
  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSearch} className="relative">
        <div className="flex">
          {/* Search Input */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                if (onChange) {
                  onChange(e.target.value)
                }
              }}
              placeholder={placeholder}
              className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-l-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Filter Button */}
          {showFilters && (
            <button
              type="button"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`px-4 py-3 border-t border-b border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasActiveFilters ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <Filter size={20} />
            </button>
          )}

          {/* Search Button */}
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-r-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Rechercher
          </button>
        </div>
      </form>

      {/* Filter Panel */}
      {showFilterPanel && showFilters && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Effacer tout
                  </button>
                )}
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange("category", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {categories.map((category) => (
                    <option key={category} value={category === "Toutes les catégories" ? "" : category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Level Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau
                </label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange("level", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {levels.map((level) => (
                    <option key={level} value={level === "Tous les niveaux" ? "" : level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix
                </label>
                <select
                  value={filters.price}
                  onChange={(e) => handleFilterChange("price", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {priceRanges.map((price) => (
                    <option key={price} value={price === "Tous les prix" ? "" : price}>
                      {price}
                    </option>
                  ))}
                </select>
              </div>

              {/* Duration Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée
                </label>
                <select
                  value={filters.duration}
                  onChange={(e) => handleFilterChange("duration", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {durations.map((duration) => (
                    <option key={duration} value={duration === "Toutes les durées" ? "" : duration}>
                      {duration}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note
                </label>
                <select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange("rating", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {ratings.map((rating) => (
                    <option key={rating} value={rating === "Toutes les notes" ? "" : rating}>
                      {rating}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowFilterPanel(false)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
              >
                Appliquer les filtres
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
